import { Component } from '@theme/component';

class StudioPurchaseHelp extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    this.bundleVariants = new WeakMap();
    this.formRoots = new WeakSet();
    this.highlightModes = new WeakMap();
    this.addonLists = new WeakSet();
    this.addonDisclosures = new WeakMap();
    this.desktop = matchMedia('(min-width:750px)');
    this.layoutDesktop = matchMedia('(min-width:1000px)');
    this.layoutNodes = [];
    this.layoutObserver = new ResizeObserver(() => this.schedule());
    this.formObservers = [];
    this.main = document.getElementById('MainContent');
    if (!this.main) return;
    const signal = this.controller.signal;
    window.addEventListener('click', event => this.pumperCheckout(event), {signal,capture:true});
    this.observer = new MutationObserver(records => {
      if (records.some(r => {const target=r.target instanceof Element ? r.target : r.target.parentElement;return target && !target.closest('studio-purchase-help');})) this.schedule();
    });
    this.observer.observe(this.main, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['lb-variant-id']});
    this.main.addEventListener('change', () => this.schedule(), {signal});
    this.main.addEventListener('input', () => this.schedule(), {signal});
    this.desktop.addEventListener('change', () => this.schedule(), {signal});
    this.layoutDesktop.addEventListener('change', () => this.schedule(), {signal});
    this.main.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('shopify-accelerated-checkout,more-payment-options-link')) this.expressAddons();
      if (this.desktop.matches && event.target instanceof Element && event.target.closest('.cs-product-feature-details>summary,.cs-category-details>summary')) event.preventDefault();
    }, {signal,capture:true});
    this.refresh();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();this.observer?.disconnect();
    this.layoutObserver?.disconnect();
    this.main?.style.removeProperty('--cs-desktop-continuation');
    this.main?.style.removeProperty('--cs-desktop-info-offset');
    for (const observer of this.formObservers || []) observer.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.checkoutTimer);
  }
  schedule() {
    if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame=0;this.refresh();});
  }
  refresh() {
    this.desktopLayout();
    if (this.dataset.contact === 'true') {this.enquiry();return;}
    for (const wrapper of this.main.querySelectorAll('.product-details .quantity-selector-wrapper')) {
      const label=wrapper.querySelector('input[name="quantity"]')?.getAttribute('aria-label');
      if (label && wrapper.dataset.csQuantityLabel !== label) wrapper.dataset.csQuantityLabel=label;
    }
    this.highlights();
    this.addons();
    const picker = this.main.querySelector('variant-picker[data-template-product-match="true"]');
    let variant;
    try {variant=JSON.parse(picker?.querySelector('script[type="application/json"]')?.textContent || '{}');} catch {return;}
    const nativeId = String(variant.id || this.main.querySelector('.product-details input[name="id"]')?.value || '');
    if (!nativeId) return;
    const context = this.main.querySelector('[data-cs-pumper-context]');
    const productId = picker?.dataset.productId || context?.closest('product-price')?.dataset.productId;
    for (const widget of this.main.querySelectorAll('lb-upsell-widget')) {
      // Only the bundle's trigger card follows the main variant. Add-on variants stay independent.
      const card = widget.querySelector('[trigger-card]');
      const select = card?.querySelector('select.lb-variant-picker');
      if (!card || !select || (productId && card.getAttribute('lb-product-id') !== productId)) continue;
      if (this.bundleVariants.get(widget) === nativeId) continue;
      if (![...select.options].some(option => option.value === nativeId)) continue;
      this.bundleVariants.set(widget,nativeId);
      if (select.value !== nativeId) {
        select.value=nativeId;
        select.dispatchEvent(new Event('change',{bubbles:true}));
      }
    }
    for (const link of this.main.querySelectorAll('[data-studio-product-advice]')) {
      const url = new URL(link.href,location.origin);
      if (variant.title) url.searchParams.set('variant',variant.title);
      const productUrl = new URL(location.pathname,location.origin);
      productUrl.searchParams.set('variant',nativeId);
      url.searchParams.set('url',productUrl.pathname+productUrl.search);
      if (link.href !== url.href) link.href=url.href;
    }
    for (const widget of this.main.querySelectorAll('lb-upsell-widget:has([trigger-card])')) {
      const button=widget.querySelector('.lb-bundle-cta-button .lb-button');
      const total=widget.querySelector('.lb-bundle-cta-total-cont .lb-price-sale-price')?.textContent.trim();
      if (!button || !total || button.querySelector('svg') || button.getAttribute('aria-disabled') === 'true') continue;
      const count=1+widget.querySelectorAll('input.lb-card-checkbox:checked').length;
      const label=this.dataset.bundleLabel.replace('__COUNT__',String(count)).replace('__TOTAL__',total);
      if (button.textContent.trim() !== label) button.textContent=label;
    }
    if (document.documentElement.lang === 'de') {
      for (const amount of this.main.querySelectorAll('lb-upsell-widget .lb-price-sale-price,lb-upsell-widget .lb-price-regular-price,lb-upsell-widget .lb-price-compare-price,[data-testid="sticky-price-display"] .price,[data-testid="sticky-price-display"] .compare-at-price')) {
        for (const node of amount.childNodes) if (node.nodeType === Node.TEXT_NODE) {
          const formatted=node.textContent.replace(/^\s*€\s*([\d.]+,\d{2})\s*$/, '$1 €');
          if (formatted !== node.textContent) node.textContent=formatted;
        }
      }
    }
  }
  desktopLayout() {
    const grid=this.main.querySelector('.product-information__grid.cs-product-three');
    const nodes=['.product-details','.product-information__media','.cs-product-highlights'].map(selector=>grid?.querySelector(`:scope>${selector}`));
    if (!this.layoutDesktop.matches || !CSS.supports('grid-template-rows','subgrid') || !this.main.querySelector(':scope>[id$="__section_QEQQmr"]') || nodes.some(node=>!node)) {
      this.layoutObserver.disconnect();this.layoutNodes=[];
      this.main.style.removeProperty('--cs-desktop-continuation');
      this.main.style.removeProperty('--cs-desktop-info-offset');
      return;
    }
    if (nodes.some((node,index)=>node!==this.layoutNodes[index])) {
      this.layoutObserver.disconnect();this.layoutNodes=nodes;
      for (const node of nodes) this.layoutObserver.observe(node);
    }
    const outerHeight=node=>{
      const style=getComputedStyle(node);
      return node.getBoundingClientRect().height+(parseFloat(style.marginBlockStart)||0)+(parseFloat(style.marginBlockEnd)||0);
    };
    // Reserve only the height actually needed below the gallery/highlights.
    // A shorter purchase column can continue above the left-column boundary.
    const remainder=outerHeight(nodes[0])-Math.max(outerHeight(nodes[1]),outerHeight(nodes[2]));
    for (const [property,value] of [['--cs-desktop-continuation',Math.max(0,remainder)],['--cs-desktop-info-offset',Math.min(0,remainder)]]) {
      const size=`${Math.round(value*100)/100}px`;
      if (this.main.style.getPropertyValue(property)!==size) this.main.style.setProperty(property,size);
    }
  }
  highlights() {
    for (const details of this.main.querySelectorAll('.cs-product-feature-details,.cs-category-details')) {
      const desktop=this.desktop.matches;
      if (this.highlightModes.get(details) === desktop) continue;
      const initialized=this.highlightModes.has(details);
      this.highlightModes.set(details,desktop);
      if (desktop || initialized || details.matches(".cs-product-feature-details")) details.open=desktop;
      const summary=details.querySelector('summary');
      if (!summary) continue;
      if (desktop) {
        summary.setAttribute('tabindex','-1');
        summary.setAttribute('role','heading');
        summary.setAttribute('aria-level','2');
      } else {
        summary.removeAttribute('tabindex');
        summary.removeAttribute('role');
        summary.removeAttribute('aria-level');
      }
    }
  }
  addons() {
    // Pumper inserts its plain container before the form after app blocks hydrate.
    // Put quantity tiers first without reconnecting the native accessory widget.
    for (const form of this.main.querySelectorAll('.product-details product-form-component')) {
      const tiers=form.querySelector(':scope>#prvw__bundle_container');
      const accessories=form.querySelector(':scope>.shopify-app-block:has(.lb-addon-cont)');
      if (tiers && accessories && (tiers.compareDocumentPosition(accessories) & Node.DOCUMENT_POSITION_PRECEDING)) form.insertBefore(tiers,accessories);
    }
    for (const container of this.main.querySelectorAll('.product-details .lb-addon-cont')) this.addonDisclosure(container);
    this.expressAddons();
    for (const list of this.main.querySelectorAll('.product-details .lb-addon-cont>.lb-list')) {
      if (this.addonLists.has(list) || list.children.length < 2) continue;
      this.addonLists.add(list);
      const head=list.parentElement.querySelector('.lb-addon-head');
      const existing=head?.querySelector('.cs-addon-navigation') || list.parentElement.querySelector(':scope>.cs-addon-navigation');
      const controls=existing || document.createElement('div');
      controls.className='cs-addon-navigation';
      const previous=document.createElement('button'), next=document.createElement('button'), count=document.createElement('span');
      previous.type=next.type='button';
      previous.textContent='‹';next.textContent='›';
      previous.setAttribute('aria-label',this.dataset.previousAddon);
      next.setAttribute('aria-label',this.dataset.nextAddon);
      count.className='cs-addon-position';
      controls.replaceChildren(previous,count,next);
      if (head && controls.parentElement !== head) head.append(controls);
      const cards=()=>[...list.children].filter(card=>card.matches('[product-card]'));
      const index=()=> {
        const bounds=list.getBoundingClientRect(), rtl=getComputedStyle(list).direction==='rtl';
        let nearest=0,distance=Infinity;
        cards().forEach((card,i)=>{
          const rect=card.getBoundingClientRect();
          const delta=Math.abs(rtl ? bounds.right-rect.right : rect.left-bounds.left);
          if(delta<distance){distance=delta;nearest=i;}
        });
        return nearest;
      };
      const update=()=>{
        const current=index(), total=cards().length;
        count.textContent=(current+1)+' / '+total;
        previous.disabled=current===0;next.disabled=current===total-1;
      };
      const move=delta=>{
        const items=cards(), current=Math.max(0,Math.min(items.length-1,index()+delta));
        const target=items[current], bounds=list.getBoundingClientRect(), rect=target?.getBoundingClientRect();
        if(!rect) return;
        const rtl=getComputedStyle(list).direction==='rtl';
        list.scrollBy({left:rtl ? rect.right-bounds.right : rect.left-bounds.left,behavior:'instant'});
      };
      const signal=this.controller.signal;
      previous.addEventListener('click',()=>move(-1),{signal});
      next.addEventListener('click',()=>move(1),{signal});
      list.addEventListener('scroll',update,{signal,passive:true});
      this.desktop.addEventListener('change',update,{signal});
      update();
    }
  }
  expressAddons() {
    // Shopify's product-page express checkout accepts these additional-item fields.
    // Selleasy continues to own the regular add-to-cart action and its app components.
    for (const component of this.main.querySelectorAll('.product-details product-form-component')) {
      const form=component.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) continue;
      const items=new Map();
      for (const card of component.querySelectorAll('.lb-addon-cont [product-card]')) {
        if (!card.querySelector('input.lb-card-checkbox:checked')) continue;
        const id=card.getAttribute('lb-variant-id');
        const quantity=Number(card.querySelector('input.lb-qty-count')?.value || 1);
        if (!/^\d+$/.test(id || '') || !Number.isSafeInteger(quantity) || quantity < 1) continue;
        items.set(id,(items.get(id) || 0)+quantity);
      }
      const signature=JSON.stringify([...items]);
      let fields=form.querySelector(':scope>[data-cs-express-addons]');
      if (!items.size) {fields?.remove();continue;}
      if (fields?.dataset.csExpressAddons === signature) continue;
      if (!fields) {
        fields=document.createElement('div');
        fields.hidden=true;
        form.append(fields);
      }
      fields.dataset.csExpressAddons=signature;
      const inputs=[];
      let index=1;
      for (const [id,quantity] of items) {
        for (const [key,value] of [['id',id],['quantity',String(quantity)]]) {
          const input=document.createElement('input');
          input.type='hidden';
          input.name=`shopify-additional-items[${index}][${key}]`;
          input.setAttribute('value',value);
          inputs.push(input);
        }
        index++;
      }
      fields.replaceChildren(...inputs);
    }
  }
  pumperCheckout(event) {
    if (!(event.target instanceof Element)) return;
    const link=event.target.closest('more-payment-options-link');
    const component=link?.closest('product-form-component');
    if (!component || !this.main.contains(component) || !component.querySelector('#prvw__bundle_container')) return;
    const selected=[...component.querySelectorAll('.lb-addon-cont [product-card]:has(input.lb-card-checkbox:checked)')];
    if (!selected.length) return;
    const button=[...component.querySelectorAll('form[data-type="add-to-cart-form"] button[name="add"]')].find(node=>node.getClientRects().length);
    if (!button || button.disabled) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (this.checkoutPending) return;
    this.checkoutPending=true;
    const expected=selected.map(card=>card.getAttribute('lb-variant-id'));
    const complete=event=>{
      const items=event.detail?.resource?.items;
      if (!Array.isArray(items) || !expected.every(id=>items.some(item=>String(item.variant_id || item.id)===id))) return;
      cleanup();
      window.location.assign((window.Shopify?.routes?.root || '/')+'checkout');
    };
    const cleanup=()=>{
      clearTimeout(this.checkoutTimer);
      document.removeEventListener('cart:update',complete);
      this.checkoutPending=false;
    };
    document.addEventListener('cart:update',complete,{signal:this.controller.signal});
    // Let Pumper and Selleasy build the discounted cart together before leaving it.
    // If an app fails, keep its normal cart/error interface available for retry.
    this.checkoutTimer=setTimeout(cleanup,12000);
    button.click();
  }
  addonDisclosure(container) {
    const head=container.querySelector('.lb-addon-head'), list=container.querySelector(':scope>.lb-list');
    if (!head || !list || !list.children.length) return;
    let state=this.addonDisclosures.get(container);
    if (!state || !head.contains(state.summary)) {
      head.querySelector('.cs-addon-toggle')?.remove();
      const summary=head.querySelector('.cs-addon-summary') || document.createElement('div');
      summary.className='cs-addon-summary';
      const label=document.createElement('strong'), status=document.createElement('span');
      label.textContent=this.dataset.addonLabel;label.setAttribute('role','heading');label.setAttribute('aria-level','2');
      status.className='cs-addon-status';status.setAttribute('aria-live','polite');status.setAttribute('aria-atomic','true');
      summary.replaceChildren(label,status);
      if (!head.contains(summary)) head.prepend(summary);
      state={summary,status};this.addonDisclosures.set(container,state);
      container.addEventListener('input',()=>this.schedule(),{signal:this.controller.signal});
      container.addEventListener('click',()=>this.schedule(),{signal:this.controller.signal});
    }
    const selected=list.querySelectorAll('input.lb-card-checkbox:checked').length;
    const description=(selected ? this.dataset.addonSelected : this.dataset.addonOptional).replace('__COUNT__',String(selected));
    if (state.status.textContent !== description) state.status.textContent=description;
    container.classList.remove('cs-addon-collapsed');
    container.classList.toggle('cs-addon-selected',selected>0);
    for (const card of list.querySelectorAll('[product-card]')) {
      const checkbox=card.querySelector('input.lb-card-checkbox');
      const title=card.querySelector('.lb-card-title')?.textContent.trim();
      if (checkbox && title) {
        const label=this.dataset.addonCheckbox.replace('__PRODUCT__',title);
        if (checkbox.getAttribute('aria-label') !== label) checkbox.setAttribute('aria-label',label);
      }
    }
  }
  enquiry() {
    const labelFields = root => {
      for (const message of root.querySelectorAll('textarea[name="custom#nachricht"]')) {
        const label=message.parentElement?.querySelector('label');
        if (!message.id && label?.htmlFor) message.id=label.htmlFor;
      }
    };
    labelFields(this.main);
    for (const embed of this.main.querySelectorAll('shopify-forms-embed')) {
      const root=embed.shadowRoot;
      if (!root) continue;
      labelFields(root);
      if (!this.formRoots.has(root)) {
        this.formRoots.add(root);
        const observer=new MutationObserver(()=>{labelFields(root);if (!this.prefilled) this.enquiry();});
        observer.observe(root,{subtree:true,childList:true});this.formObservers.push(observer);
      }
    }
    const params = new URLSearchParams(location.search);
    const product=(params.get('product')||'').slice(0,250), variant=(params.get('variant')||'').slice(0,150);
    if (!product) return;
    const summary=[product,variant !== 'Default Title' ? variant : ''].filter(Boolean).join(' · ');
    const panel=this.querySelector('.studio-enquiry-context');
    if (panel) {
      panel.hidden=false;panel.querySelector('[data-enquiry-summary]').textContent=summary;
      const link=panel.querySelector('[data-enquiry-product]');
      const path=params.get('url')||'';
      if (path.startsWith('/products/') && !path.startsWith('//')) {
        const url=new URL(path,location.origin);
        if(url.origin===location.origin){link.href=url.href;link.textContent=product;link.hidden=false;}
      }
    }
    if (this.prefilled) return;
    const fill = root => {
      const message=root.querySelector('textarea[name="custom#nachricht"],textarea[name="contact[body]"]');
      if (!message || message.value || this.prefilled) return;
      const introduction=this.dataset.message.replace('__PRODUCT__',product).replace('__VARIANT__',variant === 'Default Title' ? '' : variant);
      Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype,'value').set.call(message,introduction+'\n\n');
      message.dispatchEvent(new Event('input',{bubbles:true,composed:true}));
      message.dispatchEvent(new Event('change',{bubbles:true,composed:true}));
      this.prefilled=true;
    };
    fill(this.main);
    for (const embed of this.main.querySelectorAll('shopify-forms-embed')) {
      if (!embed.shadowRoot) continue;
      fill(embed.shadowRoot);
      if (!this.formRoots.has(embed.shadowRoot) && !this.prefilled) {
        this.formRoots.add(embed.shadowRoot);
        const observer=new MutationObserver(()=>fill(embed.shadowRoot));
        observer.observe(embed.shadowRoot,{subtree:true,childList:true});this.formObservers.push(observer);
      }
    }
  }
}
if (!customElements.get('studio-purchase-help')) customElements.define('studio-purchase-help',StudioPurchaseHelp);
